import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import styles from './ids-calendar-event.scss';
import { customElement, scss } from '../../core/ids-decorators';
import { isValidBooleanValue, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { stripTags } from '../../utils/ids-xss-utils/ids-xss-utils';
import { attributes } from '../../core/ids-attributes';
import { type IdsColorValueCategories } from '../../utils/ids-color-utils/ids-color-utils';
import { IdsMonthViewToolTipSettings } from '../ids-month-view/ids-month-view-common';
import IdsGlobal from '../ids-global/ids-global';
import IdsTooltip from '../ids-tooltip/ids-tooltip';

export type CalendarEventData = {
  id: string;
  subject: string;
  shortSubject?: string;
  icon?: string;
  headerIcons?: { icon: string, tooltip?: string, color?: string }[];
  rsvpState?: RsvpState;
  comments?: string;
  location?: string;
  status?: string;
  starts: string;
  ends: string;
  type: string;
  isAllDay: string | boolean;
};

export type CalendarEventTypeData = {
  id: string;
  label: string;
  translationKey: string;
  color: IdsColorValueCategories;
  checked: boolean;
  disabled?: boolean;
  icon: string;
};

export type CalendarEventFilterOption = {
  label?: string;
  value: any;
  color?: IdsColorValueCategories;
  checked?: boolean;
  disabled?: boolean
};

export type CalendarEventFilterData = {
  id: string;
  title?: string;
  options: Array<CalendarEventFilterOption>
};

export enum RsvpState {
  Pending = 'pending',
  Declined = 'declined',
  Attending = 'attending',
  NotAttending = 'notAttending'
}

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

@customElement('ids-calendar-event')
@scss(styles)
export default class IdsCalendarEvent extends Base {
  // Property used to position overlapping events in month view
  #order = 0;

  cssClasses: string[] = [];

  #dateKey = '';

  cachedEvent: CalendarEventData | null = null;

  cachedEventType: CalendarEventTypeData | null = null;

  #showTooltip = false;

  #tooltipSettings: IdsMonthViewToolTipSettings = {
    subject: true
  };

  headerIconTooltips: { template: string, id: string, target: string }[] = [];

  constructor(showTooltip?: boolean, tooltipSettings?: IdsMonthViewToolTipSettings) {
    super();

    this.#showTooltip = showTooltip ?? false;
    this.#tooltipSettings = tooltipSettings ?? this.#tooltipSettings;
  }

  /**
   * Returns array of observed attributes
   * @returns {Array<string>} attribute names
   */
  static get attributes(): string[] {
    return [
      ...super.attributes,
      attributes.DISPLAY_COMMENTS,
      attributes.HEIGHT,
      attributes.WIDTH,
      attributes.OVERFLOW,
      attributes.X_OFFSET,
      attributes.Y_OFFSET
    ];
  }

  /**
   * Invoked when ids-calendar-event is added to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#attachEventHandlers();
    this.refreshContent();
    this.recalc();
    this.#attachHeaderIconTooltips();
  }

  /**
   * Add header icon tooltips to the DOM
   * @private
   */
  #attachHeaderIconTooltips(): void {
    this.headerIconTooltips.forEach((tooltip) => {
      const existingTooltip: IdsTooltip | null | undefined = this.shadowRoot?.querySelector(`.icons #${tooltip.id}`);
      if (!existingTooltip || existingTooltip.target) {
        return;
      }

      existingTooltip.target = this.shadowRoot?.querySelector(`.icons #${tooltip.target}`);
    });
  }

  /**
   * Creates template for ids calendar event
   * @returns {string} html
   */
  template(): string {
    return `
      <a class="ids-calendar-event" href="#" color="${this.color}">
        ${this.contentTemplate()}
      </a>
    `;
  }

  /**
   * Creates template for calendar event content
   * @returns {string} content html
   */
  contentTemplate(): string {
    if (!this.eventData) return ``;

    this.headerIconTooltips = [];
    const { headerIconsTemplate, headerIconsTooltipTemplate } = this.#buildHeaderIcons();
    const conditionalClasses = this.#getConditionalClasses();
    const {
      displayTime, displayComments, text, tooltip, overflow, icon
    } = this.#getContentData();

    return `<div class="calendar-event-content ${conditionalClasses}">
      <div class="container">
        <div class="header">
          ${icon}
          <ids-text class="calendar-event-title" inline font-size="12" ${!this.#showTooltip ? `overflow="${overflow}"` : ''} tooltip="${tooltip}">
          ${text}
          </ids-text>
          <div class="icons">${headerIconsTemplate} ${headerIconsTooltipTemplate}</div>
        </div>
        <ids-text font-size="12" font-weight="lighter" class="calendar-event-time">${displayTime}</ids-text>
        <ids-text font-size="12" font-weight="lighter" line-clamp="2" class="calendar-event-comments">${displayComments}</ids-text>
      </div>
    </div>`;
  }

  /**
   * Get content data for template
   * @private
   * @returns {object} content data
   */
  #getContentData() {
    const isAllday = isValidBooleanValue(this.eventData!.isAllDay) && stringToBool(this.eventData!.isAllDay);
    const displayTime = !isAllday ? stripTags(this.getDisplayTime()) : '';
    const displayComments = this.displayComments ? stripTags(this.eventData!.comments || '') : '';
    const text = this.eventData!.shortSubject || this.eventData!.subject;
    const tooltip = this.disabled ? '' : this.#createTooltipTemplate();
    const overflow = this.overflow;
    const icon = this.eventData!.icon ? `<ids-icon class="calendar-event-icon" icon="${this.eventData!.icon}" height="14" width="14" aria-label="${this.eventData!.icon} icon"></ids-icon>` : '';

    return {
      displayTime, displayComments, text, tooltip, overflow, icon
    };
  }

  /**
   * Get conditional CSS classes
   * @private
   * @returns {string} conditional classes
   */
  #getConditionalClasses(): string {
    return this.eventData!.rsvpState === RsvpState.Pending ? RsvpState.Pending : '';
  }

  /**
   * Build header icons template
   * @private
   * @returns {object} header icons templates
   */
  #buildHeaderIcons() {
    let headerIconsTemplate = '';
    let headerIconsTooltipTemplate = '';
    let iconIndex = 0;

    for (const headerIcon of this.eventData?.headerIcons || []) {
      const statusColor = headerIcon.color ? `status-color="${stripTags(headerIcon.color)}" ` : '';
      const tooltipText = headerIcon.tooltip;
      const ariaLabel = stripTags(headerIcon.tooltip || `${headerIcon.icon} icon`);
      const safeHeaderIcon = stripTags(headerIcon.icon);

      if (tooltipText) {
        const id = stripTags(`icon-${this.dateKey}-${iconIndex}`) as string;
        headerIconsTemplate += `<ids-icon id="${stripTags(id)}" class="calendar-event-header-icon" icon="${safeHeaderIcon}" ${statusColor} height="14" width="14" aria-label="${ariaLabel}"></ids-icon>`;

        if (!this.dateKey) {
          continue;
        }

        const tooltipId = `${id}-tooltip`;
        const safeTooltipText = stripTags(tooltipText);
        this.headerIconTooltips.push({ template: `<ids-tooltip target="#${id}" id="${tooltipId}">${safeTooltipText}</ids-tooltip>`, target: id, id: tooltipId });
        headerIconsTooltipTemplate += `<ids-tooltip id="${tooltipId}">${safeTooltipText}</ids-tooltip>`;
        continue;
      }

      headerIconsTemplate += `<ids-icon class="calendar-event-header-icon" icon="${safeHeaderIcon}" ${statusColor} height="14" width="14" aria-label="${ariaLabel}"></ids-icon>`;
      iconIndex++;
    }

    return { headerIconsTemplate, headerIconsTooltipTemplate };
  }

  #createTooltipTemplate(): string {
    if (!this.eventData) return ``;

    if (this.#tooltipSettings.custom) return this.#tooltipSettings.custom;

    const startDt = this.localeAPI.formatDate(this.eventData.starts);
    const endDt = this.localeAPI.formatDate(this.eventData.ends);

    const subject = this.#tooltipSettings.subject ? this.eventData.subject : '';
    const time = this.#tooltipSettings.time ? `<br>${startDt} - ${endDt}` : '';
    const comments = this.#tooltipSettings.comments && this.eventData.comments ? `<br>${this.eventData.comments}` : '';
    const template = `${subject}${time}${comments}`;

    return template.trim();
  }

  /**
   * Attach calendar-event event handlers
   */
  #attachEventHandlers(): void {
    const triggerFn = (evt: MouseEvent) => {
      evt.preventDefault();
      evt.stopPropagation();

      if (this.disabled) return;

      this.triggerEvent(`${evt.type}calendarevent`, this, {
        detail: { elem: this },
        bubbles: true,
        cancelable: true,
        composed: true
      });
    };

    this.onEvent('click', this.container, (evt: MouseEvent) => { triggerFn(evt); });
    this.onEvent('dblclick', this.container, (evt: MouseEvent) => { triggerFn(evt); });
  }

  /** Respond to language changes */
  onLanguageChange = () => {
    this.refreshContent();
  };

  /**
   * Refreshses calendar event content with current settings
   */
  refreshContent(): void {
    if (this.container) {
      this.container.innerHTML = this.contentTemplate();
    }
  }

  /**
   * Updates calendar event dimensions and position
   */
  recalc(): void {
    if (!this.container) return;

    // resize
    if (this.height) {
      this.container.style.height = this.height;
    }

    if (this.width) {
      this.container.style.width = this.width;
    }

    // reposition
    if (this.yOffset) {
      this.container.style.top = this.yOffset;
    }

    if (this.localeAPI?.isRTL()) {
      this.container.style.right = this.xOffset || '';
      this.container.style.removeProperty('left');
    } else {
      this.container.style.removeProperty('right');
      this.container.style.left = this.xOffset || '';
    }
  }

  /**
   * Creates localized hour range string (ex. 12-5:00pm)
   * @returns {string} localized hour range
   */
  getDisplayTime(): string {
    if (!this.displayTime) {
      return '';
    }

    const startHours = this.startDate.getHours() + (this.startDate.getMinutes() / 60);
    const endHours = this.endDate.getHours() + (this.startDate.getMinutes() / 60);

    return this.localeAPI?.formatHourRange(startHours, endHours, { dateFormat: 'HH:mm' });
  }

  get localeAPI() {
    return IdsGlobal.getLocale();
  }

  /**
   * Sets calendar event data
   * @param {CalendarEventData} data Event data
   */
  set eventData(data: CalendarEventData | undefined | null) {
    this.cachedEvent = data ?? null;
    if (data) this.setAttribute(attributes.DATA_ID, data.id);
    this.refreshContent();
  }

  /**
   * Gets calendar event data
   * @returns {CalendarEventData} Event data
   */
  get eventData(): CalendarEventData | null {
    return this.cachedEvent;
  }

  /**
   * Sets calendar event type
   * @param {CalendarEventTypeData} data Event type
   */
  set eventTypeData(data: CalendarEventTypeData | undefined | null) {
    this.cachedEventType = data ?? null;

    // update cached event data
    if (this.cachedEvent && data?.id) {
      this.cachedEvent.type = data?.id;
    }

    if (data?.color) {
      this.container?.setAttribute('color', data?.color);
    } else {
      this.container?.removeAttribute('color');
    }
  }

  /**
   * Gets calendar event type
   * @returns {CalendarEventTypeData} Event type
   */
  get eventTypeData(): CalendarEventTypeData | null {
    return this.cachedEventType;
  }

  /**
   * Sets top style of calendar event
   * @param {string | null} value css top value
   */
  set yOffset(value: string | null) {
    if (value) {
      this.setAttribute(attributes.Y_OFFSET, value);
    } else {
      this.removeAttribute(attributes.Y_OFFSET);
    }

    this.recalc();
  }

  /**
   * Gets y offset
   * @returns {string | null} yOffset value
   */
  get yOffset(): string | null {
    return this.getAttribute(attributes.Y_OFFSET);
  }

  /**
   * Sets horizontal position of calendar event
   * Styles left or right depending on rtl flag
   * @param {string | null} value css left/right value
   */
  set xOffset(value: string | null) {
    if (value) {
      this.setAttribute(attributes.X_OFFSET, value);
    } else {
      this.removeAttribute(attributes.X_OFFSET);
    }

    this.recalc();
  }

  /**
   * @returns {string | null} xOffset value
   */
  get xOffset(): string | null {
    return this.getAttribute(attributes.X_OFFSET);
  }

  /**
   * Sets height of calendar event (Defaults to 20px)
   * @param {string | null} value css height value
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
    } else {
      this.removeAttribute(attributes.HEIGHT);
    }

    this.recalc();
  }

  /**
   * Gets height
   * @returns {string | null} height value
   */
  get height(): string | null {
    return this.getAttribute(attributes.HEIGHT);
  }

  /**
   * Sets width of calendar event (Defaults to width of container)
   * @param {string | null} value css width value
   */
  set width(value: string | null) {
    if (value) {
      this.setAttribute(attributes.WIDTH, value);
    } else {
      this.removeAttribute(attributes.WIDTH);
    }

    this.recalc();
  }

  /**
   * Gets width
   * @returns {string | null} width value
   */
  get width(): string | null {
    return this.getAttribute(attributes.WIDTH);
  }

  /**
   * Sets extra css classes to calendar event
   * @param {Array<string>} value array of css classes
   */
  set cssClass(value: string[]) {
    this.cssClasses = this.cssClasses.concat(value);
    this.container?.classList.add(...value);
  }

  /**
   * Gets start date of calendar event
   * @returns {Date} start date
   */
  get startDate(): Date {
    return new Date(this.cachedEvent?.starts ?? '');
  }

  /**
   * Gets end date of calendar event
   * @returns {Date} end date
   */
  get endDate(): Date {
    return new Date(this.cachedEvent?.ends ?? '');
  }

  /**
   * Gets duration of event in hours
   * @returns {number} duration in hours
   */
  get duration(): number {
    const hoursInMs: number = (1000 * 60 * 60);
    return (this.endDate.getTime() - this.startDate.getTime()) / hoursInMs;
  }

  /**
   * Sets whether hour range should be displayed in content
   * @param {boolean} value shows hour range if true
   */
  set displayTime(value: boolean) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.DISPLAY_TIME, '');
    } else {
      this.removeAttribute(attributes.DISPLAY_TIME);
    }

    this.refreshContent();
  }

  /**
   * Gets displayTime setting value
   * @returns {boolean} return true if displayTime is set
   */
  get displayTime(): boolean {
    return this.hasAttribute(attributes.DISPLAY_TIME);
  }

  /**
   * Sets whether comments should be displayed in calendar events
   * @param {boolean} value shows comments if true
   */
  set displayComments(value: boolean) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.DISPLAY_COMMENTS, '');
    } else {
      this.removeAttribute(attributes.DISPLAY_COMMENTS);
    }

    this.refreshContent();
  }

  /**
   * Gets displayComments setting value
   * @returns {boolean} return true if displayComments is set
   */
  get displayComments(): boolean {
    return this.hasAttribute(attributes.DISPLAY_COMMENTS);
  }

  /**
   * Sets overflow type for IdsText
   * @param {string} value Overflow values for IdsText
   */
  set overflow(value: string | null) {
    this.setAttribute(attributes.OVERFLOW, value ?? 'ellipsis');
    this.refreshContent();
  }

  /**
   * Gets overflow value of IdsText
   * @returns {string} IdsText overflow setting value
   */
  get overflow(): string {
    return this.getAttribute(attributes.OVERFLOW) || 'ellipsis';
  }

  /**
   * Sets order property
   * @param {number} val order number
   */
  set order(val: number) {
    this.#order = val;
  }

  /**
   * Gets order property
   * @returns {number} order number
   */
  get order(): number {
    return this.#order;
  }

  /**
   * Gets color property from event type data
   * @returns {string} color
   */
  get color(): string {
    return this.eventTypeData?.color || 'blue';
  }

  /**
   * Sets dateKey property
   * @param {string} val dateKey string
   */
  set dateKey(val: string) {
    this.#dateKey = val;
  }

  /**
   * Gets dateKey property
   * @returns {string} dateKey string
   */
  get dateKey(): string {
    return this.#dateKey;
  }

  /**
   * Set disabled property
   * @param {boolean} val disabled value
   */
  set disabled(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.DISABLED, '');
    } else {
      this.removeAttribute(attributes.DISABLED);
    }
  }

  /**
   * Get disabled property
   * @returns {boolean} disabled value
   */
  get disabled(): boolean {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Gets the tooltip settings
   * @returns {IdsMonthViewToolTipSettings} the tooltip settings
   */
  get tooltipSettings(): IdsMonthViewToolTipSettings {
    return this.#tooltipSettings;
  }

  /**
   * sets the tooltip settings
   * @param {IdsMonthViewToolTipSettings} settings the tooltip settings
   */
  set tooltipSettings(settings: IdsMonthViewToolTipSettings) {
    this.#tooltipSettings = settings;
  }
}
